import { useApi } from 'api/api-context';
import { Box, Group, Stack, Tooltip } from '@mantine/core';
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import P3Medium from 'components/typography/P3Medium';
import P3Regular from 'components/typography/P3Regular';
import InputMention from 'pages/revisions-module/template-editor/editors/InputMention';
import { IAutomation } from 'pages/revisions-module/template-editor/editors/pipeline/types';

/**
 * Formats the organization label for the tooltip.
 */
const formatOrganizationLabel = ({ organizationName, status }: { organizationName: string; status: boolean }) =>
  `${status ? '' : '[VYRADENÁ] '}${organizationName}`;

/**
 * Formats the selected organization count using Slovak plural forms.
 */
const formatSelectedOrganizationsCount = (count: number) => {
  if (count === 1) {
    return '1 vybraná organizácia';
  }

  if (count >= 2 && count <= 4) {
    return `${count} vybrané organizácie`;
  }

  return `${count} vybraných organizácií`;
};

/**
 * Formats a tooltip row containing both organization ID and label.
 */
const formatOrganizationTooltipLabel = (organizationId: number, organizationLabel: string) =>
  organizationLabel === `#${organizationId}` ? `(#${organizationId})` : `(#${organizationId}) ${organizationLabel}`;

/**
 * Displays shared automation-level conditions in a compact preview.
 */
export default function AutomationConditionsPreview({ data }: { data: IAutomation }) {
  const { getAction } = useApi();
  const conditions: ReactElement[] = [];
  const customerOrganizationIds = data.customerOrganizationIds ?? [];
  const customerOrganizationIdsKey = customerOrganizationIds.join(',');
  const [customerOrganizationLabels, setCustomerOrganizationLabels] = useState<string[] | null>(null);
  const [shouldLoadCustomerOrganizations, setShouldLoadCustomerOrganizations] = useState(false);

  useEffect(() => {
    setCustomerOrganizationLabels(null);
    setShouldLoadCustomerOrganizations(false);
  }, [customerOrganizationIdsKey]);

  useEffect(() => {
    if (
      customerOrganizationIds.length === 0 ||
      !shouldLoadCustomerOrganizations ||
      customerOrganizationLabels !== null
    ) {
      return;
    }

    let isActive = true;
    const organizationList = getAction('OrganizationList');

    organizationList({
      query: {
        filters: {
          'organizationId.in': customerOrganizationIds,
          'showDiscarded.eq': '1',
        },
      },
    }).then((organizations) => {
      if (isActive) {
        const organizationsById = new Map(
          organizations.map((organization) => [organization.organizationId, formatOrganizationLabel(organization)])
        );

        setCustomerOrganizationLabels(
          customerOrganizationIds.map((organizationId) => organizationsById.get(organizationId) ?? `#${organizationId}`)
        );
      }
    });

    return () => {
      isActive = false;
    };
  }, [customerOrganizationIds, customerOrganizationLabels, getAction, shouldLoadCustomerOrganizations]);

  /**
   * Tooltip content listing all configured customer organizations.
   */
  const customerOrganizationsTooltip = useMemo(
    () => (
      <Stack spacing={2}>
        {customerOrganizationIds.map((organizationId, index) => (
          <P3Regular key={organizationId}>
            {formatOrganizationTooltipLabel(organizationId, customerOrganizationLabels?.[index] ?? 'Načítavam...')}
          </P3Regular>
        ))}
      </Stack>
    ),
    [customerOrganizationIds, customerOrganizationLabels]
  );

  if (data.runIfTrue) {
    conditions.push(
      <Group key="run-if-true" spacing={4}>
        <P3Medium>Podmienka:</P3Medium>
        <P3Regular>
          <InputMention prefix="Iba ak platí" name={data.runIfTrue} />
        </P3Regular>
      </Group>
    );
  }

  if (customerOrganizationIds.length > 0) {
    const count = customerOrganizationIds.length;

    conditions.push(
      <Group key="customer-organizations" spacing={4}>
        <P3Medium>Zákazník:</P3Medium>
        <Tooltip label={customerOrganizationsTooltip} openDelay={200} multiline withinPortal>
          <Box
            onMouseEnter={() => setShouldLoadCustomerOrganizations(true)}
            onFocusCapture={() => setShouldLoadCustomerOrganizations(true)}
          >
            <P3Regular span sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>
              {formatSelectedOrganizationsCount(count)}
            </P3Regular>
          </Box>
        </Tooltip>
      </Group>
    );
  }

  if (conditions.length === 0) {
    return null;
  }

  return (
    <>
      {conditions.map((condition, index) => (
        <Fragment key={index}>
          {index > 0 && <span>&#x7c;</span>}
          {condition}
        </Fragment>
      ))}
    </>
  );
}
